import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext'; // Needed for authenticated requests

// 1. Create the Context
const EmailVerificationContext = createContext();

// 2. Create the Custom Hook
export const useEmailVerification = () => {
    const context = useContext(EmailVerificationContext);
    if (!context) {
        throw new Error('useEmailVerification must be used within an EmailVerificationProvider');
    }
    return context;
};

// 3. Create the Provider Component
export const EmailVerificationProvider = ({ children }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    // Get the authenticated request function from the Auth context
    const { makeAuthenticatedRequest } = useAuth();

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:9090';

    // Helper function to handle API responses
    const handleApiResponse = async (response) => {
        const contentType = response.headers.get("content-type");
        let data = {};
        if (contentType && contentType.indexOf("application/json") !== -1) {
            data = await response.json();
        }

        if (!response.ok) {
            if (response.status === 400) {
                throw new Error(data.error || 'Invalid request.');
            } else if (response.status === 500) {
                throw new Error(data.error || 'Internal server error.');
            }
            throw new Error(data.error || 'An unexpected error occurred.');
        }
        return data;
    };

    const clearMessages = useCallback(() => {
        setError(null);
        setSuccess(null);
    }, []);
    
    // This is the ADMIN-ONLY function that sends the email and gets the code back.
    const sendAndGetVerificationCode = useCallback(async (email) => {
        if (!email || !/\S+@\S+\.\S+/.test(email)) {
            const validationError = 'A valid email address is required.';
            setError(validationError);
            throw new Error(validationError);
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            // CORRECTED: The URL now points to the simplified admin endpoint.
            const url = `${API_BASE_URL}/admin/email-verification/send-code`;
            const requestBody = { email };

            // Use the authenticated request function to ensure only admins can call this.
            const response = await makeAuthenticatedRequest(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestBody),
            });

            const result = await handleApiResponse(response);
            
            const successMessage = result.message || 'Verification code sent successfully.';
            setSuccess(successMessage);

            // Return the full result object, which includes the code from the backend.
            return {
                success: true,
                message: successMessage,
                code: result.code,
            };

        } catch (err) {
            const errorMessage = err.message || 'Failed to send verification code.';
            setError(errorMessage);
            throw new Error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [API_BASE_URL, makeAuthenticatedRequest]);

    // Define the context value
    const value = {
        loading,
        error,
        success,
        // Expose the single, correct function for the component to use.
        sendAndGetVerificationCode,
        clearMessages,
    };

    return (
        <EmailVerificationContext.Provider value={value}>
            {children}
        </EmailVerificationContext.Provider>
    );
};